import { format, isBefore, parseISO, startOfDay } from 'date-fns'

/**
 * Hard coded class info for the marketing pages.
 *
 * Starting Swing is a new listing on Commuvity every month. The only part of
 * this file that changes month to month is the MONTHLY UPDATE block below;
 * every date, label and sign up link on the site is worked out from it.
 * The update-monthly-classes skill (.claude/skills) rewrites that block.
 */
export const STORE_URL = 'https://commuvity.com/store/1'

/** Where members log in to see their tickets and membership */
export const PROFILE_URL = `${STORE_URL}/profile`

export const venue = {
  name: 'The Link Auditorium',
  neighborhood: 'Central West End',
  address: '4504 Westminster Pl, St. Louis, MO 63108',
  description:
    'A thriving learning center and event space in the Central West End neighborhood of St. Louis. All classes and dances take place upstairs.',
  mapUrl:
    'https://www.google.com/maps/search/?api=1&query=4504+Westminster+Pl%2C+St.+Louis%2C+MO+63108',
}

// ─── MONTHLY UPDATE: START ───────────────────────────────────────────────────
// Copied from the newest "Starting Swing in <Month>" listing on Commuvity.
//   eventId: the number in the listing's address, commuvity.com/store/1/events/<eventId>
//   dates:   every class date in the series, as YYYY-MM-DD
export const startingSwingSeries = {
  eventId: 1056,
  name: 'Starting Swing in November',
  dates: ['2026-11-04', '2026-11-11', '2026-11-18', '2026-11-25'],
}
// ─── MONTHLY UPDATE: END ─────────────────────────────────────────────────────

const seriesDays = startingSwingSeries.dates.map((date) => parseISO(date))
const today = startOfDay(new Date())
const firstDay = seriesDays[0]
const lastDay = seriesDays[seriesDays.length - 1]
const firstDayText = format(firstDay, 'EEE, MMM d')

/**
 * Where the series is relative to today, so the site never advertises a past
 * date as upcoming if a monthly update is late.
 */
const seriesStatus: 'upcoming' | 'running' | 'ended' = isBefore(today, firstDay)
  ? 'upcoming'
  : isBefore(lastDay, today)
    ? 'ended'
    : 'running'

export const startingSwing = {
  name: startingSwingSeries.name,
  headline: 'Every class includes a 2 hour social dance',
  signupUrl: `${STORE_URL}/events/${startingSwingSeries.eventId}/purchase`,
  status: seriesStatus,
  /** e.g. "Wed, Oct 7" */
  startDate: firstDayText,
  /** e.g. "Starts Wed, Oct 7". Reads as a label or the start of a sentence */
  startLine: {
    upcoming: `Starts ${firstDayText}`,
    running: `Started ${firstDayText}`,
    ended: 'A new series starts every month',
  }[seriesStatus],
  /** e.g. "The next series starts Wed, Oct 7". A full clause for body text */
  nextSeriesText: {
    upcoming: `The next series starts ${firstDayText}`,
    running: `The current series started ${firstDayText}`,
    ended: 'A new series starts every month',
  }[seriesStatus],
  dates: seriesDays.map((day) => format(day, 'EEE, MMM d')),
  classCount: seriesDays.length,
  day: 'Wednesdays',
  time: '7:30 PM - 8:30 PM',
  pricePerClass: '$25',
  studentDeal: '50% off for students',
  level: 'No experience required',
  levelDescription:
    "For those who have never danced before. We'll take your office-chair-shaped body and mold it into the lithe form of a Greek deity with an MFA in dance.",
  description: [
    'A study recently found that 95% of the time, people who take this class become hopelessly addicted, find love, discover the meaning of life, and win the lottery.',
    "If you are part of the 5% who don't win the lottery, it doesn't matter. Because you'll have so much fun that it'll feel like you won 20 lotteries. You're the real winner, since you'll look like a million bucks on the dance floor. It's also important to note, I guess, that you'll receive dance instruction from some of the most experienced instructors in the Midwest.",
    "This is a 4-class progressive series, but we don't do \"just classes.\" That's lame. How are you gonna get any good at dancing if you don't actually DO that dance? Easy answer. You won't.",
    "Which is why we have a 2 hour social event after every single class, the cost of which is included in your purchase. You can stay for none of it, some of it, or all of it. It's a good time, you'll meet friends, and you'll get to practice all the things you just learned in class.",
  ],
  whatYouWillLearn: [
    'How to dance with anyone',
    'How to look confident dancing at weddings, parties, and socials',
    'Complex footwork and turn patterns',
    'How to literally read minds and fly',
  ],
  prerequisites: [
    'A desire to learn a new, challenging, athletic hobby',
    'Confidence in yourself to learn',
    'At least one leg',
  ],
}

export const wednesdayNightSwing = {
  name: 'Wednesday Night Swing',
  headline: 'Our weekly social dance',
  signupUrl: `${STORE_URL}/events/381/purchase`,
  day: 'Every Wednesday',
  time: '8:30 PM - 10:30 PM',
  price: '$10',
  description:
    "Are you a normal, well-adjusted, functioning member of society? That's great, and we are happy for you. Granted, we at STL Swing have no idea what that's like. We are dance addicts who need our weekly fix, and this is how we get it. We'll have great music, great people, and a great vibe. While we might be degenerates who wasted our lives learning to swing dance, we still welcome anyone and everyone to join us. No matter who you are, you'll have a great time. This is an event for everyone. Want to just see what swing dancing looks like? Want to just listen to music and chill out? Want to meet some new friends? Wednesday Night Swing has got you covered.",
}

export const memberships = {
  url: `${STORE_URL}/memberships`,
  socialOnly: {
    name: 'Wednesday Night Swing King',
    price: '$8.33 per month, billed yearly',
  },
  everything: {
    name: 'Weekly Classes & Dances',
    price: 'The price of one series + $30, you get all classes free',
  },
}

/** How class prices are written everywhere on the site */
export const CLASS_PRICE = '$25 / class, includes 2 hour social event'

export const curriculum = {
  /** Continuing Swing teaches one of these each month, in this order, then repeats */
  continuingTopics: [
    {
      name: 'Jumps & Kicks',
      focus: 'Charleston',
      advanced: 'Advanced Jumps & Kicks',
    },
    {
      name: 'Turns & Spins',
      focus: 'Turn patterns',
      advanced: 'Advanced Turns & Spins',
    },
    {
      name: 'Classic Swing',
      focus: 'Swing out focus',
      advanced: 'Advanced Classic Swing',
    },
  ],
  electives: ['Tap', 'Blues', 'Balboa', 'Bachata', 'Solo Jazz', 'STL Shag'],
}
